import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HeartHandshake, Leaf, Wheat, Sun, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const VALUES = [
  { icon: Wheat, title: "Fatto a mano, davvero", text: "Sfoglia tirata a mattarello, dolci che sanno di casa, conserve in piccoli lotti. Niente scorciatoie industriali." },
  { icon: Leaf, title: "Vegetale per scelta", text: "Cuciniamo soprattutto vegetariano e vegano, non per moda ma perché ci piace. E si sente nel piatto." },
  { icon: Sun, title: "La stagione comanda", text: "Il menu e gli scaffali della bottega cambiano con il mercato. Se non c'è, non è stagione: pazienza, tornerà." },
  { icon: HeartHandshake, title: "Rapporto diretto", text: "Chi scrive, chi cucina e chi consegna sono le stesse persone. Ci conosciamo per nome e ci piace così." },
];

export default function ChiSiamo() {
  useSeo({
    title: "Chi Siamo | Brunch & Bottega, progetto di cucina a Rimini",
    description: "La storia di Brunch & Bottega: un piccolo progetto indipendente di cucina a Rimini, tra brunch a domicilio, pasta fresca e conserve fatte a mano.",
    path: "/chi-siamo",
  });

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="chisiamo-hero">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Chi siamo</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink">
            Una piccola cucina indipendente, <em className="text-forest">con le mani in pasta.</em>
          </h1>
          <div className="mt-6 space-y-4 text-base sm:text-lg leading-relaxed text-stone-600 max-w-2xl">
            <p>
              Brunch & Bottega nasce a Rimini da un'idea semplice: cucinare bene per le persone,
              senza aprire un ristorante. Da una parte portiamo il brunch e la cucina a domicilio;
              dall'altra produciamo in piccoli lotti quello che finisce sugli scaffali della bottega.
            </p>
            <p>
              Siamo [NOME FONDATORE/FONDATRICE — da completare]: [BREVE STORIA PERSONALE — da completare,
              ad esempio come è nato l'amore per la cucina e perché proprio a Rimini].
            </p>
            <p>
              Quello che non cambia mai: ingredienti di stagione, lavoro artigianale,
              e il piacere di vedere una tavola svuotarsi in fretta.
            </p>
          </div>
        </motion.div>
        <motion.div className="lg:col-span-5" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-forest/10 border border-stone-200/60">
            <img
              src="/images/pasta-display.jpg"
              alt="Pasta fresca artigianale esposta in bottega"
              className="w-full h-80 lg:h-[420px] object-cover"
              fetchPriority="high"
              data-testid="chisiamo-hero-image"
            />
          </div>
        </motion.div>
      </section>

      <section className="bg-sand/70 border-y border-stone-200/60" data-testid="chisiamo-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
            In cosa crediamo (oltre che nel burro)
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-7">
            {VALUES.map((v, i) => (
              <motion.article key={v.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="bg-white/80 border border-stone-200/80 rounded-2xl p-7 shadow-sm"
                data-testid={`value-card-${i}`}>
                <v.icon size={28} className="text-terracotta" aria-hidden="true" />
                <h3 className="font-display text-xl mt-4 text-ink">{v.title}</h3>
                <p className="mt-2 text-sm sm:text-base leading-relaxed text-stone-600">{v.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center" data-testid="chisiamo-cta">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
          Vuoi conoscerci? <em className="text-forest">Si fa prima a tavola.</em>
        </motion.h2>
        <motion.div {...fadeUp} className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/brunch" data-testid="chisiamo-cta-brunch"
            className="inline-flex justify-center items-center gap-2 bg-forest hover:bg-forest-dark text-cream font-medium px-7 py-3.5 rounded-full transition-all shadow-sm active:scale-95">
            Prenota il tuo brunch <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to="/contatti" data-testid="chisiamo-cta-contatti"
            className="inline-flex justify-center items-center gap-2 border border-forest text-forest hover:bg-forest/10 font-medium px-7 py-3.5 rounded-full transition-all active:scale-95">
            Scrivici
          </Link>
        </motion.div>
      </section>
    </>
  );
}
